<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import Odometer from '../misc/Odometer.vue';
import { getQuantitySizeSet, type ButtonBehaviour, type Size } from '../../composables/useButtonStyles';
import Button from './Button.vue'
import { CartProductService } from '@/services-new/cartProductService'
import { useCartStore } from '@/stores/CartStore'

const props = defineProps({
  size: {
    type: String as () => Size,
    default: 'md',
  },
  initLabel: {
    type: String as () => 'none' | 'add' | 'toCart',
    default: 'none',
  },
  responsiveMinify: {
    type: Boolean,
    default: false
  },
  behaviour: {
    type: String as () => ButtonBehaviour,
    default: 'static',
  },
  initButtonClass: {
    type: String,
    default: ''
  },
  productId: {
    type: Number as () => number | null,
    default: null,
  },
});

const cartStore = useCartStore()
const cartProductService = new CartProductService()

const localNumber = ref(0)
const isLoading = ref(false)

const number = computed(() =>
  props.productId !== null ? cartStore.getProductQuantity(props.productId) : localNumber.value
)

// TODO: replace with i18n translate keys
const cartLabel = {
  none: { icon: 'mi-plus', label: '' },
  add: { icon: 'mi-plus', label: 'Přidat' },
  toCart: { icon: 'mi-cart-fill', label: 'Do košíku' }
}

const { size } = toRefs(props);

const sizeSet = computed(() => getQuantitySizeSet(size.value, props.behaviour))

const handleAdd = async () => {
  if (props.productId !== null) {
    isLoading.value = true
    try {
      await cartProductService.add(props.productId)
    } finally {
      isLoading.value = false
    }
  } else {
    localNumber.value++
  }
}

const handleIncrement = async () => {
  if (props.productId !== null) {
    isLoading.value = true
    try {
      await cartProductService.increment(props.productId)
    } finally {
      isLoading.value = false
    }
  } else {
    localNumber.value++
  }
}

const handleDecrement = async () => {
  if (props.productId !== null) {
    isLoading.value = true
    try {
      await cartProductService.decrement(props.productId)
    } finally {
      isLoading.value = false
    }
  } else if (localNumber.value > 0) {
    localNumber.value--
  }
}
</script>

<template>
  <template v-if="number === 0">
    <span :class="[responsiveMinify ? 'hidden lg:flex' : 'flex', initButtonClass]">
      <Button
        :label="cartLabel[initLabel].label"
        :icon="isLoading ? 'mi-refresh animate-spin' : cartLabel[initLabel].icon"
        severity="success"
        :size="size"
        class="flex-1"
        :behaviour="behaviour"
        :disabled="isLoading"
        @click.stop="handleAdd"
      />
    </span>
    <span
      v-if="responsiveMinify"
      class="flex lg:hidden"
    >
      <Button
        :icon="isLoading ? 'mi-refresh animate-spin' : 'mi-plus'"
        severity="success"
        size="xs"
        class="relative"
        :disabled="isLoading"
        @click.stop="handleAdd"
      />
    </span>
  </template>
  <div
    v-else
    class="bg-yellow-100 p-1 rounded-full flex flex-row"
  >
    <button
      class="rounded-full flex bg-yellow-300 hover:bg-yellow-400 text-brown-600 items-center justify-center"
      :class="[sizeSet.sizing, sizeSet.icon]"
      :disabled="isLoading"
      @click.stop="handleDecrement"
    >
      <i
        class="mi-minus"
        :class="sizeSet.icon"
      />
    </button>
    <div
      class="flex justify-center items-center text-brown-600 flex-1 font-roboto-slab"
      :class="sizeSet.label"
    >
      <Odometer
        :number="number"
        effect="pop"
        class="w-full"
      />
    </div>
    <button
      class="rounded-full bg-green-500 hover:bg-green-700 text-white flex cursor-pointer active:scale-90 transition items-center justify-center"
      :class="[sizeSet.sizing, sizeSet.icon]"
      :disabled="isLoading"
      @click.stop="handleIncrement"
    >
      <i
        class="mi-plus"
        :class="sizeSet.icon"
      />
    </button>
  </div>
</template>
